'use client';

import { cn } from '@/lib/utils';

interface LoadingProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'blue' | 'purple' | 'pink' | 'indigo' | 'orange' | 'green';
  text?: string;
  showLogo?: boolean;
  fullScreen?: boolean;
}

const sizeClasses = {
  sm: 'w-8 h-8',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
  xl: 'w-20 h-20'
};

const variantClasses = {
  default: 'from-green-900 via-emerald-900 to-green-900',
  blue: 'from-blue-900 via-purple-900 to-indigo-900',
  purple: 'from-purple-900 via-indigo-900 to-purple-900',
  pink: 'from-indigo-900 via-purple-900 to-pink-900',
  indigo: 'from-indigo-900 via-blue-900 to-purple-900',
  orange: 'from-orange-900 via-amber-900 to-orange-900',
  green: 'from-green-900 via-emerald-900 to-green-900'
};

const backgroundElements = {
  default: [
    { color: 'bg-green-500', position: '-top-40 -right-40 w-80 h-80' },
    { color: 'bg-emerald-500', position: '-bottom-40 -left-40 w-80 h-80' },
    { color: 'bg-lime-500', position: 'top-40 left-40 w-60 h-60' }
  ],
  blue: [
    { color: 'bg-purple-500', position: '-top-40 -right-40 w-80 h-80' },
    { color: 'bg-blue-500', position: '-bottom-40 -left-40 w-80 h-80' },
    { color: 'bg-indigo-500', position: 'top-40 left-40 w-60 h-60' }
  ],
  purple: [
    { color: 'bg-indigo-500', position: '-top-40 -right-40 w-80 h-80' },
    { color: 'bg-purple-500', position: '-bottom-40 -left-40 w-80 h-80' },
    { color: 'bg-blue-500', position: 'top-40 left-40 w-60 h-60' }
  ],
  pink: [
    { color: 'bg-pink-500', position: '-top-40 -right-40 w-80 h-80' },
    { color: 'bg-indigo-500', position: '-bottom-40 -left-40 w-80 h-80' },
    { color: 'bg-purple-500', position: 'top-40 left-40 w-60 h-60' }
  ],
  indigo: [
    { color: 'bg-indigo-500', position: '-top-40 -right-40 w-80 h-80' },
    { color: 'bg-blue-500', position: '-bottom-40 -left-40 w-80 h-80' },
    { color: 'bg-purple-500', position: 'top-40 left-40 w-60 h-60' }
  ],
  orange: [
    { color: 'bg-orange-500', position: '-top-40 -right-40 w-80 h-80' },
    { color: 'bg-amber-500', position: '-bottom-40 -left-40 w-80 h-80' },
    { color: 'bg-yellow-500', position: 'top-40 left-40 w-60 h-60' }
  ],
  green: [
    { color: 'bg-green-500', position: '-top-40 -right-40 w-80 h-80' },
    { color: 'bg-emerald-500', position: '-bottom-40 -left-40 w-80 h-80' },
    { color: 'bg-lime-500', position: 'top-40 left-40 w-60 h-60' }
  ]
};

export function Loading({
  className,
  size = 'lg',
  variant = 'default',
  text = 'Yükleniyor...',
  showLogo = true,
  fullScreen = true
}: LoadingProps) {
  const spinnerSize = sizeClasses[size];
  const bgVariant = variantClasses[variant];
  const bgElements = backgroundElements[variant];

  const content = (
    <>
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {bgElements.map((element, index) => (
          <div
            key={index}
            className={cn(
              'absolute rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse-slow',
              element.color,
              element.position,
              index === 0 && 'animate-float',
              index === 1 && 'animation-delay-2000',
              index === 2 && 'animation-delay-4000'
            )}
          />
        ))}
        
        {/* Additional floating elements */}
        <div className="absolute top-20 right-20 w-4 h-4 bg-white/20 rounded-full animate-float animation-delay-2000"></div>
        <div className="absolute bottom-20 right-1/3 w-2 h-2 bg-white/30 rounded-full animate-float animation-delay-4000"></div>
        <div className="absolute top-1/3 left-20 w-3 h-3 bg-white/25 rounded-full animate-float"></div>
      </div>

      <div className="relative z-10 text-center animate-fade-in">
        {/* Logo */}
        {showLogo && (
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-8 border border-gray-200 animate-scale-in shadow-lg">
            <img
              src="/images/yesil.png"
              alt="Nestro Logo"
              className="w-12 h-12 object-contain"
            />
          </div>
        )}

        {/* Loading spinner */}
        <div className="relative mb-8 animate-slide-in-up">
          <div className={cn('mx-auto relative', spinnerSize)}>
            {/* Outer rotating ring */}
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-white/80 border-r-white/60 animate-spin"></div>
            
            {/* Middle rotating ring - opposite direction */}
            <div className="absolute inset-1 rounded-full border-3 border-transparent border-b-white/60 border-l-white/40 animate-spin-reverse animation-delay-1000"></div>
            
            {/* Inner rotating ring */}
            <div className="absolute inset-2 rounded-full border-2 border-transparent border-t-white/40 border-r-white/30 animate-spin animation-delay-2000"></div>
            
            {/* Center pulsing dot */}
            <div className="absolute inset-4 bg-gradient-to-br from-white/90 to-white/60 rounded-full animate-pulse-slow shadow-lg"></div>
            
            {/* Glow effect */}
            <div className="absolute inset-0 rounded-full bg-white/10 blur-sm animate-pulse"></div>
          </div>
        </div>

        {/* Loading text */}
        <div className="space-y-2 animate-slide-in-up animation-delay-2000">
          <p className="text-white/70 text-sm">
            {text}
          </p>
        </div>

        {/* Loading dots */}
        <div className="flex justify-center space-x-1 mt-6 animate-fade-in animation-delay-4000">
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce"></div>
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce animation-delay-2000"></div>
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce animation-delay-4000"></div>
        </div>
      </div>
    </>
  );

  if (fullScreen) {
    return (
      <div className={cn(
        'min-h-screen bg-gradient-to-br flex items-center justify-center p-4 relative overflow-hidden animate-gradient',
        bgVariant,
        className
      )}>
        {content}
      </div>
    );
  }

  return (
    <div className={cn(
      'flex items-center justify-center p-8 relative overflow-hidden rounded-lg bg-gradient-to-br animate-gradient',
      bgVariant,
      className
    )}>
      {content}
    </div>
  );
}

// Inline loading spinner for buttons and small areas
export function LoadingSpinner({
  className,
  size = 'sm'
}: {
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}) {
  const sizeMap = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <div className={cn('relative', sizeMap[size], className)}>
      {/* Outer rotating ring */}
      <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-white/80 border-r-white/60 animate-spin"></div>
      
      {/* Inner rotating ring - opposite direction */}
      <div className="absolute inset-0.5 rounded-full border border-transparent border-b-white/60 border-l-white/40 animate-spin-reverse animation-delay-500"></div>
      
      {/* Center dot */}
      <div className="absolute inset-1 bg-white/70 rounded-full animate-pulse-slow"></div>
    </div>
  );
}

// Loading skeleton for content areas
export function LoadingSkeleton({
  className,
  lines = 3,
  showAvatar = false
}: {
  className?: string;
  lines?: number;
  showAvatar?: boolean;
}) {
  return (
    <div className={cn('animate-pulse', className)}>
      <div className="flex items-start space-x-4">
        {showAvatar && (
          <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
        )}
        <div className="flex-1 space-y-2">
          {Array.from({ length: lines }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-4 bg-gray-300 rounded',
                i === lines - 1 ? 'w-3/4' : 'w-full'
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}